/* eslint-disable solid/reactivity */
import { PopupProps, StandardProps } from './kind';
import {
  WIDGET_NAMES,
  WIDGET_KIND,
  WIDGET_SOURCE,
  VERSION,
} from './types/constants';
import { SearchProps } from './types/search';
import {
  close,
  open,
  toggle,
  openModalForm,
} from './utils/commands/help-drawer';
import { Logger } from './utils/logger';

type WidgetIdentifier =
  | (Pick<SearchProps, 'accessToken'> & Partial<Pick<SearchProps, 'apiKey'>>)
  | (Pick<SearchProps, 'apiKey'> & Partial<Pick<SearchProps, 'accessToken'>>);

const isWidgetExists = (
  identifier: WidgetIdentifier,
  widgetName: string,
): boolean => {
  // find if widget already exists in the DOM
  const widget = document.querySelector(widgetName) as Element &
    WidgetIdentifier;

  // determine if the widget with same access token or api key already exists
  if (identifier.accessToken) {
    return widget?.accessToken === identifier.accessToken;
  }
  if (identifier.apiKey) {
    return widget?.apiKey === identifier.apiKey;
  }

  return false;
};

const isValidProps = (
  props: StandardProps | PopupProps,
  widgetName: string,
): boolean => {
  if (
    props.isChromeExtension ||
    props.widgetSource === WIDGET_SOURCE.PLATFORM
  ) {
    // return true if accessToken is provided and widget is not already registered
    return (
      !!props.accessToken &&
      !isWidgetExists({ accessToken: props.accessToken }, widgetName)
    );
  } else {
    return (
      !!props.apiKey && !isWidgetExists({ apiKey: props.apiKey }, widgetName)
    );
  }
};

export const initStandard = (props: StandardProps & { id?: string }) => {
  try {
    const widgetName = WIDGET_NAMES[WIDGET_KIND.STANDARD];

    if (!isValidProps(props, widgetName)) return;

    const originalElement = props.id
      ? document.getElementById(props.id)
      : document.querySelector(widgetName);

    if (!originalElement) {
      console.warn(`<${widgetName}> element not found.`);

      return;
    }

    // Store the parent and the next sibling of the original element
    const parentElement = originalElement.parentElement;
    const nextSibling = originalElement.nextSibling;

    // Remove the original element from the DOM
    if (parentElement) {
      parentElement.removeChild(originalElement);
    }

    // Create a new element
    const newElement = document.createElement(widgetName);

    if (props.version === 'latest') {
      props.version = VERSION;
    }

    Object.assign(newElement, props);

    // Insert the new element into the DOM at the original location
    if (parentElement) {
      if (nextSibling) {
        parentElement.insertBefore(newElement, nextSibling);
      } else {
        parentElement.appendChild(newElement);
      }
    }
  } catch (error) {
    Logger.error(
      `Error initializing Search widget ${VERSION} with widgetKey: ${props.apiKey}: initStandard()`,
      error as Error,
      {
        function: 'initStandard',
        filename: 'window.ts',
        widgetKey: props.apiKey,
        version: VERSION,
      },
    );
  } finally {
    return null;
  }
};

export const initPopup = (props: PopupProps) => {
  try {
    const widgetName = WIDGET_NAMES[WIDGET_KIND.POPUP];

    if (!isValidProps(props, widgetName)) return;

    const popupElement = document.createElement(
      WIDGET_NAMES[WIDGET_KIND.POPUP],
    );
    if (props.version === 'latest') {
      props.version = VERSION;
    }

    Object.assign(popupElement, props);
    document.body.appendChild(popupElement);

    return popupElement;
  } catch (error) {
    Logger.error(
      `Error initializing Sidebar widget ${VERSION} with widgetKey: ${props.apiKey}: initPopup()`,
      error as Error,
      {
        function: 'initPopup',
        filename: 'window.ts',
        widgetKey: props.apiKey,
        version: VERSION,
      },
    );
  } finally {
    return null;
  }
};

type Brainfish = {
  SearchWidget: {
    initStandard: typeof initStandard;
  };
  HelpWidget: {
    initPopup: typeof initPopup;
    close: typeof close;
    open: typeof open;
    toggle: typeof toggle;
    openModalForm: typeof openModalForm;
  };
};

declare global {
  interface Window {
    Brainfish: Brainfish;
    BrainfishAnalytics?: {
      (method: string, ...args: any[]): void;
    };
  }
}

export const parseBrainfishSearch = () => ({
  SearchWidget: {
    initStandard,
  },
  HelpWidget: {
    initPopup,
    close,
    open,
    toggle,
    openModalForm,
  },
});

export const injectBrainfishInWindow = (brainfish: Brainfish) => {
  if (typeof window === 'undefined') return;
  if (window.Brainfish) {
    window.Brainfish = { ...window.Brainfish, ...brainfish };
  } else {
    window.Brainfish = { ...brainfish };
  }
};
